import React, { useState } from "react";
import { Modal, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios_instance from "store/constant/axiosInstance";
import { extractErrorMessage } from "utils/strings";
import { getLocalStorage } from "utils/secure";
import { ReactComponent as DeleteIcon } from "images/svg/delete.svg";
import { ReactComponent as PdfIcon } from "images/svg/pdfIcon.svg";
import { ReactComponent as FileIcon } from "images/svg/FileIcon.svg";

import DescriptionEditor from "./DescriptionEditor";
import Select from "react-select";

const user = localStorage.getItem("3MTUSER")
  ? getLocalStorage("3MTUSER")
  : null;

const userRole = user?.account_type;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    height: '44px',
    minHeight: '44px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#7117E5' : '#BCC1DE',
    '&:hover': {
      borderColor: '#BCC1DE'
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: '44px',
    padding: '0 12px',
  }),
  input: (base) => ({
    ...base,
    margin: '0px',
    paddingTop: '0',
    paddingBottom: '0',
  }),
  singleValue: (base) => ({
    ...base,
    lineHeight: '44px',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '44px',
  }),
  option: (base) => ({
    ...base,
    padding: '12px 16px',
  }),
  menu: (base) => ({
    ...base,
    marginTop: '8px',
    borderRadius: '8px',
  }),
};

export const SubmissionModal = ({ showSubmissionModal, setShowSubmissionModal }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const FellowCategoryOptions = [
    {
      label: "Select a Category",
      value: ""
    },
    {
      label: "Profile Updates/Changes",
      value: "Profile Updates"
    },
    {
      label: "Applied Learning Contents and Courses",
      value: "Applied Learning Contents and Courses"
    },
    {
      label: "Assessment/Assignment Issues",
      value: "Assessment/Assignment Issues"
    },
    {
      label: "Attendance Issues",
      value: "Attendance Issues"
    },
    {
      label: "Certificates",
      value: "Certificates"
    }
  ];
  
  const ALCCategoryOptions = [
    {
      label: "Select a Category",
      value: ""
    },
    {
      label: "Organization Data Update/Changes",
      value: "Organization Data Update/Changes"
    },
    {
      label: "Fellows Issues",
      value: "Fellows Issues"
    },
    {
      label: "Applied Learning Contents and Courses",
      value: "Applied Learning Contents and Courses"
    },
    {
      label: "Attendance Issues",
      value: "Attendance Issues"
    },
    {
      label: "Assessment/Assignment Issues",
      value: "Assessment/Assignment Issues"
    }
  ];

  const formik = useFormik({
    initialValues: {
      category: "",
      subject: "",
      description: "",
      files: null
    },
    onSubmit: handleSubmit,
    validationSchema: validationSchema
  });

  async function handleSubmit(values) {
    setLoading(true);
    const formData = new FormData();
    
    if (values.files && values.files.length > 0) {
      values.files.forEach((file) => {
        formData.append("files", file);
      });
    }
    
    formData.append("category", values.category);
    formData.append("subject", values.subject);
    formData.append("description", values.description);

    axios_instance.post("core/support-ticketing", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((data) => {
      toast.success(data?.data?.message);
      setShowSubmissionModal(false);
      formik.resetForm();
      setSelectedFiles([]);
      setLoading(false);
    })
    .catch((error) => {
      const formattedError = extractErrorMessage(error?.response);
      toast.error(formattedError);
      setLoading(false);
    });
  }

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const allowedFileTypes = ["application/pdf", "image/jpeg", "image/png"];
    const maxFileSize = 10 * 1024 * 1024;
    const maxTotalSize = 20 * 1024 * 1024; 

    for (const file of newFiles) {
      if (!allowedFileTypes.includes(file.type)) {
        toast.error(`File ${file.name} is not a PDF, JPEG, or PNG.`);
        return;
      }

      if (file.size > maxFileSize) {
        toast.error(`File ${file.name} exceeds the 10MB limit.`);
        return;
      }
    }

    const totalSize = [...selectedFiles, ...newFiles].reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxTotalSize) {
      toast.error("Total files size exceeds 20MB limit.");
      return;
    }

    const updatedFiles = [...selectedFiles, ...newFiles];
    setSelectedFiles(updatedFiles);
    formik.setFieldValue("files", updatedFiles);
    
    event.target.value = '';
  };
    
  const handleRemoveFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    formik.setFieldValue("files", newFiles.length > 0 ? newFiles : null);
  };
    
  const handleAddFiles = () => {
    document.getElementById('file').click();
  };

  return (
    <Modal show={showSubmissionModal} onHide={() => setShowSubmissionModal(false)} className="support-modal">
      <Modal.Body>
        <h4 className="support-modal-title">Report an Issue</h4>
        <form onSubmit={formik.handleSubmit} className="support-modal-form">
          <Col md={12} className="mb-3">
            <label>Choose a Category</label>
            <Select
              value={userRole === "FELLOW" 
                ? FellowCategoryOptions.find(option => option.value === formik.values.category)
                : ALCCategoryOptions.find(option => option.value === formik.values.category)
              }
              onChange={(selectedOption) => formik.setFieldValue("category", selectedOption.value)}
              options={userRole === "FELLOW" ? FellowCategoryOptions : ALCCategoryOptions}
              components={{
                IndicatorSeparator: () => null
              }}
              styles={selectStyles}
            />
            {formik.touched.category && formik.errors.category && (
              <div className="text-danger">{formik.errors.category}</div>
            )}
          </Col>
          <Col md={12} className="mb-3">
            <label>Subject</label>
            <div>
              <input 
                name="subject"
                id="subject"
                className="form-control"
                placeholder="Add a Subject"
                {...formik.getFieldProps("subject")}
              />
              {formik.touched.subject && formik.errors.subject && (
                <div className="text-danger">{formik.errors.subject}</div>
              )}
            </div>
          </Col>
          <div>
            <DescriptionEditor formik={formik} />

            <div className="mb-3">
              <label htmlFor="file" className="form-label">
                Upload Files (PDF, JPG, PNG)
              </label>
              
              <div className="upload-container border rounded">
                <div className="d-flex justify-content-between items-center">
                  <div>
                    <label htmlFor="file" className="select-file-button">
                      Select File
                    </label>
                    <span className="ms-3 text-muted">
                      {selectedFiles.length === 0 ? "No File Selected" : ""}
                    </span>
                    <input
                      className="d-none"
                      type="file"
                      id="file"
                      accept=".pdf,.jpeg,.jpg,.png"
                      onChange={handleFileChange}
                    />
                  </div>
                  <span 
                    onClick={handleAddFiles}
                    className="add-file-text"
                  >
                    Add
                  </span>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="selected-files">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="file-item d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        {/* File Preview */}
                        <div className="file-preview me-2">
                          {file.type.startsWith('image/') ? (
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="Preview" 
                              className="img-thumbnail"
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="file-icon">
                              {file.type === 'application/pdf' ? (
                                <PdfIcon style={{ width: '40px', height: '40px' }} />
                              ) : (
                                <FileIcon style={{ width: '40px', height: '40px' }} />
                              )}
                              <small className="d-block text-center">{file.type.split('/').pop()}</small>
                            </div>
                          )}
                        </div>
                        <h4 className="file-name">{file.name}</h4>
                      </div>
                      <DeleteIcon 
                        className="me-1 cursor-pointer" 
                        onClick={() => handleRemoveFile(index)}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {formik.touched.files && formik.errors.files && (
                <div className="text-danger">{formik.errors.files}</div>
              )}
            </div>

            <div className="filter-section">
              <div className="filter-group">
                <div className="score-range-container">
                  <button type="button" className="reset-filter" onClick={()=>setShowSubmissionModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="show-results-button"
                    disabled={!(formik.isValid && formik.dirty)}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : (
                      <>Submit</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

const validationSchema = Yup.object().shape({
  category: Yup.string().required("Issue Category is required"),
  subject: Yup.string().required("Subject is required"),
  description: Yup.string().required("Description is required"),
});